import { PageViewEvent } from 'analytics-api';
import { AnalyticsEvent, AnalyticsGTMSettings, DefaultAnalyticsSettings } from 'analytics-api';

// eslint-disable-next-line @typescript-eslint/naming-convention
export type genericAnalyticObject = string | genericAnalyticObject[] | {[key: string]: genericAnalyticObject} | undefined;

export class AnalyticsOyshoEventDataHelpers {

  /**
   * Devuelve un mapa con los tipos de eventos
   */
  public static EVENT_TYPES = new Map<string, () => AnalyticsGTMSettings>([
    ['pageview', AnalyticsOyshoEventDataHelpers.getEmptyPageViewSettings],
    ['popupview', AnalyticsOyshoEventDataHelpers.getEmptyPageViewSettings],
    ['analyticsEvent', AnalyticsOyshoEventDataHelpers.getEmptyAnalyticsEventsSettings],
  ]);

  public static emailRegex = /([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9_-]+)/gi;
  public static keyExceptions: string[] = ['event', 'currencyCode', 'storeId'];

  /**
   * Devuelve un objeto PageViewEvent vacío
   */
  public static getEmptyPageViewSettings(): PageViewEvent {
    return {
      event: 'pageview',
      context: {
        platform: undefined,
        codeVersion: undefined,
        storeId: undefined,
        language: undefined,
        isOpenStore: undefined,
        isCMS: true
      },
      page: {
        pageURL: undefined,
        section: undefined,
        pageType: undefined,
        pageTitle: undefined,
      },
      user: {
        isLogged: undefined,
        userId: undefined,
        userType: undefined,
        profileStatus: undefined,
        umd: undefined,
        us: undefined,
        quickBuy: undefined,
        isBuyer: undefined,
        clientType: undefined
      },
      category: {
        season: undefined,
        category: undefined,
        subcategory: undefined,
        subsubcategory: undefined,
        subsubsubcategory: undefined,
        itemsNumber: undefined,
        viewType: undefined,
        filter: undefined,
        sortType: undefined,
        crossType: undefined
      },
      product: {
        reference: undefined,
        productState: undefined,
        claseProducto: undefined,
        consumerProduct: undefined,
        onlineOnly: undefined,
        stock: undefined,
        color: undefined,
        talla: undefined,
        model: undefined,
        family: undefined,
        subfamily: undefined,
        section: undefined
      },
      photo: {
        typePhoto: undefined,
        linkType: undefined,
        lightType: undefined,
        background: undefined,
        frame: undefined
      },
      look: {
        lookReference: undefined,
        campanaReference: undefined,
        bundleReference: undefined,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        referencia_foto: undefined
      },
      searchEngine: {
        searchTerm: undefined,
        searchResultNr: undefined,
        searchType: undefined
      },
      cart: {
        cartNumberItems: undefined,
        cartNumberItemsNoStock: undefined
      },
      order: {
        orderId: undefined,
        payment: {
          type: undefined,
          giftcardAmount: undefined,
          affinity: undefined,
          netAmount: undefined,
          savedData: undefined,
          giftTicket: undefined
        },
        delivery: {
          shippingType: undefined,
          weekday: undefined,
          time: undefined
        },
        discount: {
          discountType: undefined,
          fullAmount: undefined
        }
      },
      store: {
        storeId: undefined,
        storeFound: undefined,
        zipCode: undefined
      },
      return: {
        returnType: undefined,
        returnReason: undefined
      },
      error: {
        paymentType: undefined,
        deliveryType: undefined
      },
      ecommerce: undefined
    };
  }

  /**
   * Devuelve un objeto AnalyticsEvent vacío
   */
  public static getEmptyAnalyticsEventsSettings(): AnalyticsEvent {
    return {
      event: 'analyticsEvent',
      eventData: {
        cf: '',
        category: '',
        action: '',
        label: undefined
      },
      context: {
        isCMS: true
      },
      customData: {
        user: {
          userId: undefined,
          userType: undefined,
          profileStatus: undefined,
          quickBuy: undefined,
          clientType: undefined,
          isBuyer: undefined,
          sex: undefined,
          age: undefined,
          loginType: undefined,
          umd: undefined,
          us: undefined
        },
        category: {
          season: undefined,
          category: undefined,
          subcategory: undefined,
          subsubcategory: undefined,
          subsubsubcategory: undefined,
          itemsNumber: undefined,
          viewType: undefined,
          filter: undefined,
          sortType: undefined,
          crossType: undefined
        },
        product: {
          reference: undefined,
          productState: undefined,
          claseProducto: undefined,
          consumerProduct: undefined,
          color: undefined,
          talla: undefined,
          model: undefined,
          stock: undefined,
          family: undefined,
          subfamily: undefined,
          section: undefined
        },
        cart: {
          cartNumberItems: undefined,
          cartNumberItemsNoStock: undefined
        },
        order: {
          orderId: undefined,
          orderState: undefined,
          payment: {
            savedData: undefined,
            giftTicket: undefined
          },
          discount: {
            discountType: undefined
          }
        },
        photo: {
          typePhoto: undefined,
          linkType: undefined,
          lightType: undefined,
          background: undefined,
          frame: undefined
        },
        return: {
          returnType: undefined,
          returnReason: undefined
        },
        look: {
          lookReference: undefined,
          bundleReference: undefined,
          videoName: undefined,
          campanaReference: undefined,
          // eslint-disable-next-line @typescript-eslint/naming-convention
          referencia_foto: undefined
        },
        store: {
          zipCode: undefined,
          storeId: undefined,
          dropPoint: undefined
        },
        giftCard: {
          amountEcard: undefined,
          amountFisica: undefined,
          deliveryTime: undefined
        },
        searchEngine: {
          searchTerm: undefined,
          searchType: undefined
        },
        error: {
          paymentType: undefined,
          deliveryType: undefined
        }
      },
      ecommerce: undefined
    };
  }

  public static setGTMSettings(eventData: DefaultAnalyticsSettings): AnalyticsGTMSettings {
    const helperToCall = AnalyticsOyshoEventDataHelpers.EVENT_TYPES.get(eventData.event);

    if (!isDefined(helperToCall)) {
      throw new Error('Bad event type in analytics settings');
    }

    const settings = helperToCall();
    return {
      ...settings,
      ...eventData
    };
  }

}
